import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { VeracordLogo } from '@/components/ui/VeracordLogo';

// ─────────────────────────────────────────────
// FEATURE LIST
// ─────────────────────────────────────────────
const FEATURES = [
'Every policy — locked and timestamped',
'All acknowledgments — staff names and dates',
'Complete version history — every change recorded',
'Generated in 30 seconds'];


// ─────────────────────────────────────────────
// DARK NAVY MESH
// ─────────────────────────────────────────────
const DarkMesh = () =>
<>
    <style>{`
      @keyframes ctaMesh {
        0%   { background-position: 0% 0%; }
        25%  { background-position: 100% 40%; }
        50%  { background-position: 60% 100%; }
        75%  { background-position: 10% 60%; }
        100% { background-position: 0% 0%; }
      }
      @keyframes ctaOrb1 {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(-60px, 40px) scale(1.3); }
      }
      @keyframes ctaOrb2 {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(50px,-50px) scale(1.2); }
      }
      @keyframes ctaOrb3 {
        0%,100% { transform: translate(0,0) scale(1); }
        33%     { transform: translate(40px, 60px) scale(1.15); }
        66%     { transform: translate(-40px,-30px) scale(0.9); }
      }
      @keyframes ctaOrb4 {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(30px, 30px) scale(1.1); }
      }
      @keyframes pdfFloat {
        0%,100% { transform: translateY(0px) rotate(-1.5deg); }
        50%     { transform: translateY(-10px) rotate(-1.5deg); }
      }
    `}</style>

    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
      {/* Base — deep navy shifting gradient */}
      <div style={{
      position: 'absolute', inset: 0,
      background: 'linear-gradient(135deg, #00022e 0%, #02075d 30%, #001a1a 60%, #00022e 100%)',
      backgroundSize: '300% 300%',
      animation: 'ctaMesh 18s ease-in-out infinite'
    }} />

      {/* Orb 1 — rich teal, top-left */}
      <div style={{
      position: 'absolute', top: '-10%', left: '5%',
      width: '50%', height: '60%', borderRadius: '50%',
      filter: 'blur(70px)',
      animation: 'ctaOrb1 12s ease-in-out infinite',
      background: 'radial-gradient(circle, rgba(51,153,153,0.35) 0%, rgba(13,148,136,0.15) 40%, transparent 70%)'
    }} />

      {/* Orb 2 — deep teal, bottom-right */}
      <div style={{
      position: 'absolute', bottom: '-10%', right: '5%',
      width: '45%', height: '55%', borderRadius: '50%',
      filter: 'blur(80px)',
      animation: 'ctaOrb2 15s ease-in-out infinite',
      background: 'radial-gradient(circle, rgba(51,153,153,0.3) 0%, rgba(2,7,93,0.2) 50%, transparent 70%)'
    }} />

      {/* Orb 3 — periwinkle center shimmer */}
      <div style={{
      position: 'absolute', top: '20%', left: '30%',
      width: '40%', height: '60%', borderRadius: '50%',
      filter: 'blur(90px)',
      animation: 'ctaOrb3 17s ease-in-out infinite',
      background: 'radial-gradient(circle, rgba(100,130,255,0.12) 0%, transparent 70%)'
    }} />

      {/* Orb 4 — navy highlight, bottom-left */}
      <div style={{
      position: 'absolute', bottom: '0%', left: '-5%',
      width: '35%', height: '40%', borderRadius: '50%',
      filter: 'blur(60px)',
      animation: 'ctaOrb4 10s ease-in-out infinite',
      background: 'radial-gradient(circle, rgba(15,56,150,0.4) 0%, transparent 70%)'
    }} />

      {/* Subtle noise grain overlay for texture */}
      <div style={{
      position: 'absolute', inset: 0,
      backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.03\'/%3E%3C/svg%3E")',
      opacity: 0.4,
      mixBlendMode: 'overlay'
    }} />
    </div>
  </>;


// ─────────────────────────────────────────────
// PDF THUMBNAIL
// ─────────────────────────────────────────────
const PdfThumbnail = () =>
<motion.div
  initial={{ opacity: 0, y: 40, rotate: -3 }}
  whileInView={{ opacity: 1, y: 0, rotate: -1.5 }}
  viewport={{ once: true, margin: '-60px' }}
  transition={{ duration: 0.9, ease: [0.23, 1, 0.32, 1], delay: 0.2 }}
  style={{
    position: 'relative',
    width: '100%',
    maxWidth: '480px',
    margin: '0 auto',
    animation: 'pdfFloat 6s ease-in-out infinite'
  }}>
  
    {/* Glow behind document */}
    <div style={{
    position: 'absolute', inset: '-20px',
    borderRadius: '24px',
    background: 'radial-gradient(ellipse, rgba(51,153,153,0.25) 0%, transparent 70%)',
    filter: 'blur(20px)',
    zIndex: 0
  }} />

    {/* Document frame */}
    <div style={{
    position: 'relative', zIndex: 1,
    borderRadius: '16px',
    overflow: 'hidden',
    border: '1.5px solid rgba(51,153,153,0.3)',
    boxShadow: '0 32px 80px rgba(0,2,46,0.6), 0 8px 32px rgba(51,153,153,0.2), 0 2px 8px rgba(0,0,0,0.4)'
  }}>
      {/* Teal accent top line */}
      <div style={{
      height: '3px',
      background: 'linear-gradient(90deg, #339999, #06b6d4, rgba(6,182,212,0))'
    }} />

      {/* PDF image */}
      <img
      src="/assets/audit_report_cover.png"
      alt="Veracord compliance audit report"
      style={{
        width: '100%',
        height: 'auto',
        display: 'block'
      }} />
    
    </div>

    {/* "Read-only audit record" stamp */}
    <motion.div
    initial={{ opacity: 0, scale: 0.8, rotate: 8 }}
    whileInView={{ opacity: 1, scale: 1, rotate: 8 }}
    viewport={{ once: true }}
    transition={{ delay: 0.8, duration: 0.5, ease: 'backOut' }}
    style={{
      position: 'absolute', bottom: '-12px', right: '-12px', zIndex: 2,
      background: 'rgba(0,2,46,0.95)',
      border: '1.5px solid rgba(51,153,153,0.5)',
      borderRadius: '8px',
      padding: '6px 12px',
      backdropFilter: 'blur(8px)'
    }}>
    
      <span style={{
      fontFamily: 'Inter, sans-serif',
      fontWeight: 700, fontSize: '10px',
      letterSpacing: '0.12em', textTransform: 'uppercase',
      color: '#339999'
    }}>
        Read-only audit record
      </span>
    </motion.div>
  </motion.div>;


// ─────────────────────────────────────────────
// FINAL CTA SECTION
// ─────────────────────────────────────────────
export const FinalCtaSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const parallaxScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1.05, 1.0]);

  return (
    <section
      ref={sectionRef}
      className="w-full relative overflow-x-hidden"
      style={{
        background: '#00022e',
        fontFamily: 'Inter, sans-serif',
        paddingTop: '96px',
        paddingBottom: '96px'
      }}>
      
      {/* Dark navy mesh */}
      <DarkMesh />

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative" style={{ zIndex: 1 }}>

        {/* Two column layout — PDF left, content right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">

          {/* Left — PDF thumbnail */}
          <div className="flex items-center justify-center order-2 lg:order-1">
            <motion.div style={{ y: parallaxY, scale: parallaxScale }}>
              <PdfThumbnail />
            </motion.div>
          </div>

          {/* Right — headline, features, CTA */}
          <div className="order-1 lg:order-2">

            {/* Eyebrow */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700, fontSize: '11px',
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: '#339999', marginBottom: '16px'
              }}>
              
              The Proof
            </motion.p>

            {/* Headline */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.05, ease: [0.23, 1, 0.32, 1] }}
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                marginBottom: '24px'
              }}>
              
              This Is What You
              <br />
              <span style={{ color: '#339999' }}>Hand the Auditor</span>
            </motion.h2>

            {/* Teal underline */}
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
              style={{
                height: '3px', width: '48px', borderRadius: '99px',
                background: 'linear-gradient(90deg, #339999, #06b6d4)',
                marginBottom: '36px',
                transformOrigin: 'left center'
              }} />
            

            {/* Feature list */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.25 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '44px' }}>
              
              {FEATURES.map((feature, idx) =>
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.3 + idx * 0.07 }}
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                
                  <motion.div
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: idx * 0.4 }}>
                  
                    <CheckCircle2 style={{ width: '18px', height: '18px', color: '#339999', flexShrink: 0 }} />
                  </motion.div>
                  <span style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500, fontSize: '15px',
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: 1.4
                }}>
                    {feature}
                  </span>
                </motion.div>
              )}
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.55 }}>
              
              <motion.a
                href="/get-started"
                whileHover={{ scale: 1.03, boxShadow: '0 8px 32px rgba(51,153,153,0.4)' }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2 }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #339999, #0d9488)',
                  color: '#ffffff',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 800,
                  fontSize: '16px',
                  letterSpacing: '-0.01em',
                  textDecoration: 'none',
                  boxShadow: '0 4px 24px rgba(51,153,153,0.3), 0 1px 4px rgba(0,0,0,0.2)',
                  cursor: 'pointer'
                }}>
                
                Get Started
                <ArrowRight style={{ width: '18px', height: '18px' }} />
              </motion.a>

              {/* Trust line */}
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px', fontWeight: 500,
                color: 'rgba(255,255,255,0.35)',
                marginTop: '14px',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}>
                <CheckCircle2 style={{ width: '13px', height: '13px', color: 'rgba(51,153,153,0.6)' }} />
                $249/month · or $207/mo billed annually · Cancel anytime.
              </p>
            </motion.div>

          </div>
        </div>

        {/* Footer line */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8, duration: 0.8 }}
          style={{
            marginTop: '80px',
            paddingTop: '32px',
            borderTop: '1px solid rgba(51,153,153,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
          
          <VeracordLogo size="sm" color="rgba(255,255,255,0.4)" />
          <span style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.2)',
            letterSpacing: '0.05em'
          }}>
            The System of Record
          </span>
        </motion.div>

      </div>
    </section>);

};

export default FinalCtaSection;